import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import useSopState from '@/states/sop.state';
import useTenantState from '@/states/tenant.state';
import type { ISop } from '@/states/sop.state';

type SopMethodItem = { man?: string; machine?: string; material?: string; method?: string };

export default function SOPDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTenant } = useTenantState();
  const { getSopDetail } = useSopState();

  const [sopData, setSopData] = useState<ISop | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSopDetail = async () => {
      if (!currentTenant?.id || !id) return;
      try {
        const response = await getSopDetail(currentTenant.id, id);
        if (!response) return;
        const data = (response as { data?: ISop })?.data || response;
        setSopData(data);
      } catch (error) {
        console.error('Failed to fetch SOP detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSopDetail();
  }, [currentTenant?.id, id, getSopDetail]);

  const methods: SopMethodItem[] = sopData?.sop ?? sopData?.org_sop ?? [];

  return (
    <section className="w-full px-5 pb-8">
      <div className="flex items-center gap-2 py-5">
        <button
          onClick={() => navigate(`/${currentTenant?.slug}/sop`)}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          title="Kembali ke SOP"
        >
          <ArrowLeft size={20} />
        </button>
        <span className="text-base font-bold">{`Detail SOP - ${sopData?.name ?? ''}`}</span>
      </div>

      <div className="border-b border-gray-200 mb-6" />

      {loading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : sopData ? (
        <>
          <h2 className="text-sm font-semibold mb-3">Detail</h2>

          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 w-12 font-medium">No</th>
                  <th className="px-4 py-3 font-medium">Metode</th>
                  <th className="px-4 py-3 font-medium">Manusia</th>
                  <th className="px-4 py-3 font-medium">Mesin</th>
                  <th className="px-4 py-3 font-medium">Material</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {methods.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-xs">
                      Tidak ada metode tersedia.
                    </td>
                  </tr>
                ) : (
                  methods.map((item, idx) => (
                    <tr key={idx} className="bg-white">
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">{item.method || '-'}</td>
                      <td className="px-4 py-3">{item.man || '-'}</td>
                      <td className="px-4 py-3">{item.machine || '-'}</td>
                      <td className="px-4 py-3">{item.material || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="text-gray-400 text-sm">Data tidak tersedia</p>
      )}
    </section>
  );
}
